import React, { useState } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import type { ProjectDocument, DocumentStatus } from '../../types/documents';

interface DocumentVerificationModalProps {
  document: ProjectDocument;
  onClose: () => void;
  onVerify: (status: DocumentStatus, note?: string) => Promise<void>;
}

export const DocumentVerificationModal: React.FC<DocumentVerificationModalProps> = ({
  document,
  onClose,
  onVerify
}) => {
  const [status, setStatus] = useState<DocumentStatus.VERIFIED | DocumentStatus.REJECTED>(DocumentStatus.VERIFIED);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onVerify(status, note.trim() || undefined);
      onClose();
    } catch (error) {
      console.error('Error verifying document:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Vérifier le document</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">{document.label}</h3>
            <p className="text-sm text-gray-600">{document.filename}</p>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Décision de vérification
            </label>
            <div className="space-y-2">
              <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value={DocumentStatus.VERIFIED}
                  checked={status === DocumentStatus.VERIFIED}
                  onChange={(e) => setStatus(e.target.value as DocumentStatus.VERIFIED)}
                  className="mr-3"
                />
                <Check className="w-5 h-5 text-green-600 mr-2" />
                <div>
                  <div className="font-medium text-gray-900">Approuver</div>
                  <div className="text-sm text-gray-600">Le document est conforme et validé</div>
                </div>
              </label>
              
              <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  value={DocumentStatus.REJECTED}
                  checked={status === DocumentStatus.REJECTED}
                  onChange={(e) => setStatus(e.target.value as DocumentStatus.REJECTED)}
                  className="mr-3"
                />
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <div>
                  <div className="font-medium text-gray-900">Rejeter</div>
                  <div className="text-sm text-gray-600">Le document ne respecte pas les critères</div>
                </div>
              </label>
            </div>
          </div>

          {/* Note */}
          <div>
            <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
              {status === DocumentStatus.REJECTED ? 'Motif de rejet *' : 'Commentaire (optionnel)'}
            </label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={
                status === DocumentStatus.REJECTED 
                  ? "Expliquez pourquoi le document est rejeté..."
                  : "Ajoutez un commentaire si nécessaire..."
              }
              required={status === DocumentStatus.REJECTED}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (status === DocumentStatus.REJECTED && !note.trim())}
              className={`px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                status === DocumentStatus.VERIFIED
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Traitement...</span>
                </div>
              ) : (
                status === DocumentStatus.VERIFIED ? 'Approuver' : 'Rejeter'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};